/**
 * Payment-method model for expense entries.
 *
 * An expense (`type='expense'`) is paid in one of four ways. The payment method
 * controls WHEN the expense is recognized as project cost and whether it feeds a
 * per-person ledger (advance prepaid credit / open-account payable).
 *
 * Cost-recognition timing:
 *  - cash_card (or legacy NULL): immediately
 *  - check: when the check is marked paid (is_processed + status='completed')
 *  - advance: upfront (now if sub-method cash_card, on-pay if sub-method check)
 *  - open_account: only once settled ("mark as paid")
 *
 * Advance draw-down rows (a later invoiced expense consuming a person's prepaid
 * credit) carry counts_toward_cost=false: they record the fatura but add no new
 * cost, since the cost was already recognized when the advance was paid.
 */

export type PaymentMethod = 'cash_card' | 'check' | 'advance' | 'open_account';
export type AdvanceSubMethod = 'cash_card' | 'check';

export const PAYMENT_METHODS: PaymentMethod[] = [
  'cash_card',
  'check',
  'advance',
  'open_account',
];

/** A check (or advance-by-check / open-account-by-check) has actually cleared. */
export function isCheckCleared(entry: any): boolean {
  return entry?.is_processed === true && entry?.status === 'completed';
}

/**
 * Whether a `type='expense'` row's amount should currently count toward project
 * cost. This is the single source of truth used by cost aggregation.
 */
export function isExpenseCostRecognized(entry: any): boolean {
  if (!entry) return false;

  // Draw-down rows never add cost (recognized at the advance).
  if (entry.counts_toward_cost === false) return false;

  const pm: PaymentMethod | null = entry.payment_method ?? null;

  switch (pm) {
    case null:
    case 'cash_card':
      return true;
    case 'check':
      return isCheckCleared(entry);
    case 'advance':
      return entry.advance_sub_method === 'check' ? isCheckCleared(entry) : true;
    case 'open_account':
      // An open account (Açık Hesap) is a liability, never a cost itself. Its cost
      // is realised through linked settlement payment rows (cash/check), which are
      // ordinary expenses that count on their own terms.
      return false;
    default:
      return true; // unknown/legacy -> behave like cash
  }
}

/**
 * Currencies used for ledger aggregation. Mirrors the keys used by
 * `getProjectCostsFromEntries` so ledger figures stay consistent with cost
 * figures (each currency is summed independently, no cross-conversion).
 */
export const LEDGER_CURRENCIES = [
  'TRY', 'USD', 'EUR', 'GBP', 'JPY', 'RUB', 'KWD', 'SAR', 'CHF', 'XAU', 'BTC',
] as const;

export type LedgerCurrency = (typeof LEDGER_CURRENCIES)[number];
export type CurrencyMap = Record<string, number>;

/** Sum of a row's official + unofficial converted amounts for one currency. */
export function rowAmountForCurrency(row: any, currency: string): number {
  const lc = currency.toLowerCase();
  const official = parseFloat(row?.[`amount_${lc}_official`]) || 0;
  const unofficial = parseFloat(row?.[`amount_${lc}_unofficial`]) || 0;
  return official + unofficial;
}

/** Build an empty per-currency map initialised to 0. */
export function emptyCurrencyMap(): CurrencyMap {
  return LEDGER_CURRENCIES.reduce((acc, c) => {
    acc[c] = 0;
    return acc;
  }, {} as CurrencyMap);
}

/** Add a row's per-currency amounts into an accumulator map (optionally scaled). */
export function addRowToCurrencyMap(map: CurrencyMap, row: any, sign = 1): void {
  for (const currency of LEDGER_CURRENCIES) {
    map[currency] += sign * rowAmountForCurrency(row, currency);
  }
}

/** True for rows that participate in the check-payment lifecycle (mark paid / postpone). */
export function isCheckLikeExpense(entry: any): boolean {
  if (!entry) return false;
  if (entry.payment_method === 'check') return true;
  if (entry.payment_method === 'advance' && entry.advance_sub_method === 'check') return true;
  return false;
}
